import type { Editor, Range, SuggestionListRef } from 'OpalChat/TiptapEditorTypes';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Box, Flex, Separator, Text } from '@optiaxiom/react';
import { IconPen, IconPlus, IconScrewdriverWrench } from '@optimizely/axiom-icons';

import type { SuggestionItem } from '../types';

import { useSuggestionKeyboard } from './hooks/useSuggestionKeyboard';
import { styles } from './AgentFormPromptTemplate.css';
import { SectionHeader, SuggestionItem as SuggestionItemComponent } from './Common';

export type ToolsSuggestionListProps = {
  editor: Editor;
  range: Range;
  command: (item: SuggestionItem) => void;
  query: string;
  tools?: string[];
  onAddTool: () => void;
  onEditTool: (tool: string) => void;
  onInsertTool: (editor: Editor, toolName: string) => void;
};

export const ToolsSuggestionList = forwardRef<SuggestionListRef, ToolsSuggestionListProps>(
  ({ editor, range, command, query, tools, onAddTool, onEditTool, onInsertTool }, ref) => {
    const lowerQuery = query?.toLowerCase() ?? '';

    const filteredTools = useMemo(
      () => tools?.filter(t => t.toLowerCase().includes(lowerQuery)) ?? [],
      [tools, lowerQuery]
    );

    const showAddAction = !lowerQuery || 'add tool'.includes(lowerQuery);

    const suggestionItems: SuggestionItem[] = useMemo(
      () => [
        ...filteredTools.map(tool => ({
          id: `tool:${tool}`,
          label: tool,
          type: 'tool' as const,
          data: tool,
        })),
        ...(showAddAction
          ? [
              {
                id: 'action:add-tool',
                label: 'Add tool',
                type: 'action' as const,
                actionType: 'add-tool' as const,
              },
            ]
          : []),
      ],
      [filteredTools, showAddAction]
    );

    const handleSelect = useCallback(
      (item: SuggestionItem) => {
        editor.chain().focus().deleteRange(range).run();
        if (item.type === 'tool' && item.data) {
          onInsertTool(editor, item.data as string);
        } else if (item.actionType === 'add-tool') {
          onAddTool();
        }
        command(item);
      },
      [editor, range, command, onInsertTool, onAddTool]
    );

    const handleEdit = useCallback(
      (item: SuggestionItem) => {
        if (item.type === 'tool' && item.data) {
          onEditTool(item.data as string);
        }
      },
      [onEditTool]
    );

    const { selectedId, isEditFocused, onKeyDown, handleItemClick } = useSuggestionKeyboard({
      items: suggestionItems,
      query,
      onSelect: handleSelect,
      onEdit: handleEdit,
      hasEditAction: true,
    });

    useImperativeHandle(ref, () => ({ onKeyDown }), [onKeyDown]);

    if (suggestionItems.length === 0) {
      return (
        <Box
          rounded="md"
          shadow="md"
          border="1"
          borderColor="border.default"
          bg="bg.default"
          p="6"
          className={styles.container}
          maxH="md"
        >
          <Text textAlign="center" color="fg.tertiary" fontSize="sm">
            No tools found
          </Text>
        </Box>
      );
    }

    return (
      <Flex
        flexDirection="column"
        gap="6"
        rounded="md"
        shadow="md"
        border="1"
        borderColor="border.default"
        overflow="auto"
        bg="bg.default"
        pt="6"
        pb="12"
        className={styles.container}
      >
        <SectionHeader title="Tools" />
        {filteredTools.map(tool => {
          const itemId = `tool:${tool}`;
          const isSelected = selectedId === itemId;
          return (
            <SuggestionItemComponent
              key={tool}
              iconBefore={<IconScrewdriverWrench />}
              label={tool}
              iconAfter={<IconPen />}
              isSelected={isSelected}
              isEditFocused={isSelected && isEditFocused}
              onClick={() => handleItemClick(itemId)}
              onEditClick={() => onEditTool(tool)}
            />
          );
        })}
        {showAddAction && (
          <>
            <Separator />
            <SuggestionItemComponent
              iconBefore={<IconPlus />}
              label="Add tool"
              isAction
              isSelected={selectedId === 'action:add-tool'}
              onClick={() => handleItemClick('action:add-tool')}
            />
          </>
        )}
      </Flex>
    );
  }
);

ToolsSuggestionList.displayName = 'ToolsSuggestionList';
